/**
 * useBrandKitExtraction Hook
 *
 * Manages the async brand-kit extraction flow:
 *  1. Upload file → POST /ai/brand-kits/extract
 *  2. Subscribe to BrandKitChannel via ActionCable
 *  3. Receive extraction_completed / extraction_failed events
 *  4. Return extracted brand_data for form pre-fill
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  subscribeToBrandKit,
  unsubscribeFromBrandKit,
  BrandKitExtractionCompletedData,
  BrandKitExtractionFailedData,
} from '../lib/actionCable'
import { brandKitApi, ExtractBrandKitParams } from '../lib/api/brandKits'
import { BrandKit, BrandData } from '../types/brandKit'

export type ExtractionStatus = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed'

interface UseBrandKitExtractionReturn {
  status: ExtractionStatus
  extractedBrandKit: BrandKit | null
  extractedBrandData: BrandData | null
  error: string | null
  startExtraction: (params: ExtractBrandKitParams) => Promise<void>
  reset: () => void
}

export function useBrandKitExtraction(): UseBrandKitExtractionReturn {
  const [status, setStatus] = useState<ExtractionStatus>('idle')
  const [extractedBrandKit, setExtractedBrandKit] = useState<BrandKit | null>(null)
  const [extractedBrandData, setExtractedBrandData] = useState<BrandData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const brandKitIdRef = useRef<number | null>(null)

  const cleanup = useCallback(() => {
    if (brandKitIdRef.current) {
      unsubscribeFromBrandKit(brandKitIdRef.current)
      brandKitIdRef.current = null
    }
  }, [])

  useEffect(() => cleanup, [cleanup])

  const reset = useCallback(() => {
    cleanup()
    setStatus('idle')
    setExtractedBrandKit(null)
    setExtractedBrandData(null)
    setError(null)
  }, [cleanup])

  const startExtraction = useCallback(async (params: ExtractBrandKitParams) => {
    cleanup()
    setStatus('uploading')
    setError(null)
    setExtractedBrandKit(null)
    setExtractedBrandData(null)

    try {
      const { brand_kit } = await brandKitApi.extractBrandKit(params)
      setExtractedBrandKit(brand_kit)
      brandKitIdRef.current = brand_kit.id
      setStatus('extracting')

      subscribeToBrandKit(brand_kit.id, {
        onExtractionCompleted: (data: BrandKitExtractionCompletedData) => {
          setExtractedBrandData(data.data.brand_data as BrandData)
          setExtractedBrandKit(prev =>
            prev ? { ...prev, brand_data: data.data.brand_data, status: 'active' } : null
          )
          setStatus('completed')
          if (brandKitIdRef.current) {
            unsubscribeFromBrandKit(brandKitIdRef.current)
          }
        },
        onExtractionFailed: (data: BrandKitExtractionFailedData) => {
          setError(data.data.error || 'Extraction failed')
          setStatus('failed')
          if (brandKitIdRef.current) {
            unsubscribeFromBrandKit(brandKitIdRef.current)
          }
        },
        onRejected: () => {
          setError('WebSocket subscription rejected – check authentication')
          setStatus('failed')
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start extraction')
      setStatus('failed')
    }
  }, [cleanup])

  return {
    status,
    extractedBrandKit,
    extractedBrandData,
    error,
    startExtraction,
    reset,
  }
}
